import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, ShieldCheck, Loader2, CheckCircle2, FileDown } from "lucide-react";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const CHIP = {
  NEW: "bg-emerald-50 text-emerald-700 border-emerald-200",
  UPDATE: "bg-blue-50 text-blue-700 border-blue-200",
  DUPLICATE: "bg-slate-100 text-slate-600 border-slate-200",
  CONFLICT: "bg-red-50 text-red-700 border-red-200",
  REVIEW_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function WarrantyImporter() {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doPreview = async (fn) => {
    setBusy(true); setPreview(null);
    try { const { data } = await fn(); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not parse"); }
    finally { setBusy(false); }
  };

  const previewText = () => {
    if (!text.trim()) { toast.error("Paste a warranty list first"); return; }
    doPreview(() => api.post("/warranty-plans/import/text", { text }));
  };
  const previewFile = (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    doPreview(() => api.post("/warranty-plans/import/file", fd));
    if (fileRef.current) fileRef.current.value = "";
  };

  const commit = async () => {
    const rows = (preview?.items || []).filter((i) => ["NEW", "UPDATE"].includes(i.status)).map((i) => i.data);
    if (!rows.length) { toast.error("No valid (NEW/UPDATE) rows to import"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/warranty-plans/import/commit", { rows });
      toast.success(`Imported ${data.created.length} new, ${data.updated.length} updated`);
      setPreview(null); setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-5 space-y-4" data-testid="warranty-importer-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-slate-800" />
        <div>
          <h1 className="text-xl font-bold text-slate-900">Warranty / Protection Plan Importer</h1>
          <p className="text-sm text-slate-500">Paste or upload a warranty price list — review before saving.</p>
        </div>
        <a href={`${backend}/api/india-tax/gst/purchase/template.xlsx`} className="hidden" aria-hidden />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
          <div className="text-sm font-semibold text-slate-700">Paste list (CSV / TSV)</div>
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="import-text"
            placeholder={"Part Number,Plan,Price,Total Years,Compatible Product,Territory\nNRB-00145,Microsoft EHS Plus… 2 Years,11000,2,Surface Pro 11;Surface Pro 12,INDIA"}
            className="font-mono text-[12px]" />
          <Button size="sm" onClick={previewText} disabled={busy} data-testid="preview-text-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Preview
          </Button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col justify-center items-center text-center"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); previewFile(e.dataTransfer.files?.[0]); }}>
          <UploadCloud className="h-8 w-8 text-slate-300 mb-2" />
          <div className="text-sm text-slate-600 mb-2">Drag & drop an XLSX / CSV, or</div>
          <input ref={fileRef} type="file" hidden accept=".xlsx,.xlsm,.csv" data-testid="import-file"
            onChange={(e) => previewFile(e.target.files?.[0])} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-btn">
            <FileDown className="h-4 w-4 mr-1" /> Choose file
          </Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="import-preview">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">Review</span>
            {Object.entries(preview.counts).map(([k, v]) => (
              <Badge key={k} className={"text-[10px] border " + (CHIP[k] || CHIP.DUPLICATE)}>{k.replace(/_/g, " ")}: {v}</Badge>
            ))}
            <Button size="sm" onClick={commit} disabled={busy || !preview.importable} data-testid="import-all-btn"
              className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Import all valid ({preview.importable})
            </Button>
          </div>
          <div className="divide-y divide-slate-50">
            {preview.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`preview-row-${i}`}>
                <Badge className={"text-[10px] border shrink-0 w-28 justify-center " + (CHIP[it.status] || CHIP.DUPLICATE)}>
                  {it.status.replace(/_/g, " ")}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-slate-800">{it.data.product_name || <span className="text-slate-400">(no name)</span>}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{it.data.oem_part_number} · {it.data.total_years || "?"}yr
                    {it.reasons?.length ? ` · ${it.reasons.join(", ")}` : ""}</div>
                </div>
                <div className="text-[12px] font-mono text-slate-700 shrink-0">{inr(it.data.price)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
